import React, { useEffect } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { initScrollReveal } from '@/utils/scrollReveal';
import { useTranslation } from 'react-i18next';
import Seo from '@/components/Seo';

type PriceRow = {
  name: string;
  bgn?: string;
  eur?: string;
  note?: string;
};

type PriceCard = {
  title: string;
  subtitle?: string;
  rows?: PriceRow[];
  sections?: Array<{
    title: string;
    rows: PriceRow[];
  }>;
};

const PriceTable = ({
  rows,
  headers,
  missing,
}: {
  rows: PriceRow[];
  headers: { service: string; priceBgn: string; priceEur: string };
  missing: string;
}) => (
  <div className="w-full overflow-x-auto rounded-xl border border-dental/10 bg-white/60 backdrop-blur-sm">
    <table className="w-full min-w-[520px] text-left">
      <thead>
        <tr className="text-xs uppercase tracking-wide text-dental-dark/70 border-b border-dental/10 bg-gradient-to-r from-dental/10 via-dental-mint/10 to-dental-coral/10">
          <th className="py-3 pr-4 pl-4 font-semibold">{headers.service}</th>
          <th className="py-3 px-4 font-semibold whitespace-nowrap">{headers.priceBgn}</th>
          <th className="py-3 pl-4 pr-4 font-semibold whitespace-nowrap">{headers.priceEur}</th>
        </tr>
      </thead>
      <tbody className="text-sm">
        {rows.map((row) => (
          <tr
            key={row.name}
            className="border-b border-dental/10 last:border-b-0 hover:bg-dental-light/20 transition-colors"
          >
            <td className="py-3 pr-4 pl-4 text-dental-dark">
              <div className="font-medium">{row.name}</div>
              {row.note && <div className="text-xs text-dental-dark/60 mt-1">{row.note}</div>}
            </td>
            <td className="py-3 px-4 text-dental font-semibold whitespace-nowrap">{row.bgn ?? missing}</td>
            <td className="py-3 pl-4 pr-4 text-dental-dark/80 font-semibold whitespace-nowrap">
              {row.eur ?? missing}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const PriceCardView = ({
  card,
  headers,
  missing,
}: {
  card: PriceCard;
  headers: { service: string; priceBgn: string; priceEur: string };
  missing: string;
}) => (
  <div className="bg-white rounded-2xl p-6 shadow-card border border-dental-gray/10 h-full">
    <div className="mb-4">
      <h3 className="text-xl font-display font-bold text-dental-dark">{card.title}</h3>
      {card.subtitle && <p className="text-sm text-dental-dark/70 mt-1">{card.subtitle}</p>}
    </div>

    {card.sections ? (
      <div className="space-y-6">
        {card.sections.map((section) => (
          <div key={section.title} className="space-y-3">
            <div className="inline-flex items-center rounded-full bg-dental-light/50 px-3 py-1 text-xs font-semibold text-dental-dark">
              {section.title}
            </div>
            <PriceTable rows={section.rows} headers={headers} missing={missing} />
          </div>
        ))}
      </div>
    ) : (
      <PriceTable rows={card.rows ?? []} headers={headers} missing={missing} />
    )}
  </div>
);

const Prices = () => {
  const { t } = useTranslation('prices');

  useEffect(() => {
    const cleanup = initScrollReveal();
    return cleanup;
  }, []);

  const priceCards = t('cards', { returnObjects: true }) as PriceCard[];
  const tableHeaders = t('table', { returnObjects: true }) as {
    service: string;
    priceBgn: string;
    priceEur: string;
    missing: string;
  };

  return (
    <div className="relative overflow-x-hidden">
      <Seo page="prices" />
      <Navbar />

      <main>
        {/* Hero */}
        <section className="pt-32 pb-14 bg-gradient-to-b from-white via-dental-light/5 to-white relative overflow-hidden">
          <div className="absolute top-24 right-10 w-72 h-72 bg-dental-mint/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 left-10 w-80 h-80 bg-dental/10 rounded-full blur-3xl"></div>

          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl mx-auto text-center reveal" data-direction="up">
              <h1 className="text-4xl md:text-5xl font-display font-bold leading-tight text-dental-dark">
                {t('hero.title')}
              </h1>
              <p className="mt-4 text-lg text-dental-dark/80">
                {t('hero.description')}
              </p>
            </div>
          </div>
        </section>

        {/* Price list */}
        <section className="py-20 bg-white relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-20 bg-gradient-to-b from-dental-light/5 to-white"></div>
          <div className="absolute top-20 right-0 w-80 h-80 bg-dental-light/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-0 w-60 h-60 bg-dental-coral/5 rounded-full blur-3xl"></div>

          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center mb-12 reveal" data-direction="up">
              <h2 className="text-3xl md:text-4xl font-display font-bold mb-4 text-gradient-primary inline-block relative after:content-[''] after:absolute after:-bottom-2 after:left-1/4 after:right-1/4 after:h-1 after:bg-dental-tertiary after:rounded-full">
                {t('section.title')}
              </h2>
              <p className="text-xl text-dental-dark/80 max-w-3xl mx-auto">
                {t('section.description')}
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 reveal" data-direction="up">
              {priceCards.map((card) => (
                <PriceCardView
                  key={card.title}
                  card={card}
                  headers={tableHeaders}
                  missing={tableHeaders.missing}
                />
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Prices;

